import React, { useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  Option,
  FormControl,
  FormLabel,
  Input,
  Button,
  IconButton,
} from '@mui/joy';
import { Funnel as FilterIcon, ArrowsClockwise as RefreshIcon } from '@phosphor-icons/react';
import { useStormAlerts, useStormTerritories } from '../../hooks/useStormTracker';
import { AlertsTable } from '../../components/stormtracker/AlertsTable';
import type { StormAlertFilters, EventType, SeverityLevel } from '../../types/stormtracker';

const AlertsList: React.FC = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<StormAlertFilters>({
    ordering: '-event_timestamp',
  });

  const { data: alerts, isLoading, error, isFetching, refetch } = useStormAlerts(filters);
  const { data: territories } = useStormTerritories();

  const handleFilterChange = (key: keyof StormAlertFilters, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({ ordering: '-event_timestamp' });
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography level="h2">Alert history</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={showFilters ? 'solid' : 'outlined'}
            color="neutral"
            startDecorator={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters{Object.keys(filters).filter(key => key !== 'ordering' && filters[key as keyof StormAlertFilters] !== undefined).length > 0 ? ' (active)' : ''}
          </Button>
          <IconButton aria-label="Refresh alerts" variant="outlined" color="neutral" loading={isFetching} onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>Review storm activity by location, severity, and date. Open an alert to see the affected area and create a follow-up lead.</Typography>
      {error && <Alert color="danger" sx={{ mb: 2 }} endDecorator={<Button onClick={() => refetch()} loading={isFetching}>Retry</Button>}>Could not load storm alerts.</Alert>}
      {showFilters && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              <FormControl size="sm">
                <FormLabel>Territory</FormLabel>
                <Select
                  placeholder="All territories"
                  value={filters.territory?.toString() || ''}
                  onChange={(_, value) => handleFilterChange('territory', value ? Number(value) : undefined)}
                >
                  <Option value="">All territories</Option>
                  {territories?.map((territory) => (
                    <Option key={territory.id} value={territory.id.toString()}>
                      {territory.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Event Type</FormLabel>
                <Select
                  placeholder="All types"
                  value={filters.event_type || ''}
                  onChange={(_, value) => handleFilterChange('event_type', value as EventType | undefined)}
                >
                  <Option value="">All types</Option>
                  <Option value="hail">Hail</Option>
                  <Option value="wind">Wind</Option>
                  <Option value="tornado">Tornado</Option>
                </Select>
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Severity</FormLabel>
                <Select
                  placeholder="All severities"
                  value={filters.severity || ''}
                  onChange={(_, value) => handleFilterChange('severity', value as SeverityLevel | undefined)}
                >
                  <Option value="">All severities</Option>
                  <Option value="minor">Minor</Option>
                  <Option value="moderate">Moderate</Option>
                  <Option value="severe">Severe</Option>
                  <Option value="extreme">Extreme</Option>
                </Select>
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Min Score</FormLabel>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.min_score ?? ''}
                  onChange={(e) =>
                    handleFilterChange('min_score', e.target.value ? Number(e.target.value) : undefined)
                  }
                  slotProps={{ input: { min: 0, max: 100 } }}
                />
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Date From</FormLabel>
                <Input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={(e) => handleFilterChange('date_from', e.target.value || undefined)}
                />
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Date To</FormLabel>
                <Input
                  type="date"
                  value={filters.date_to || ''}
                  onChange={(e) => handleFilterChange('date_to', e.target.value || undefined)}
                />
              </FormControl>

              <FormControl size="sm">
                <FormLabel>Sort By</FormLabel>
                <Select
                  value={filters.ordering || '-event_timestamp'}
                  onChange={(_, value) => handleFilterChange('ordering', value || '-event_timestamp')}
                >
                  <Option value="-event_timestamp">Newest First</Option>
                  <Option value="event_timestamp">Oldest First</Option>
                  <Option value="-opportunity_score">Highest Score</Option>
                  <Option value="opportunity_score">Lowest Score</Option>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="plain" color="neutral" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        {!error && <AlertsTable alerts={alerts || []} isLoading={isLoading} showTerritory />}
      </Card>
    </Box>
  );
};

export default AlertsList;
